'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

type LookupResult = {
  imei_number: string;
  serial_number: string;
  product_name: string;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_address?: string | null;
  warranty_start?: string | null;
  warranty_end?: string | null;
  notes?: string | null;
  warranty_state?: {
    label: string;
    tone: string;
  };
};

type LookupType = 'phone' | 'serial';

function formatDate(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('vi-VN').format(date);
}

function toneClass(tone?: string) {
  if (tone === 'success') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (tone === 'warning') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (tone === 'danger') return 'border-red-200 bg-red-50 text-red-700';
  return 'border-slate-200 bg-slate-50 text-slate-700';
}

export default function WarrantyLookupClient() {
  const [lookupType, setLookupType] = useState<LookupType>('phone');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LookupResult[]>([]);
  const [contactOpen, setContactOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setResults([]);
    setNotFound(false);

    if (!query.trim()) {
      setError(lookupType === 'phone' ? 'Vui lòng nhập số điện thoại.' : 'Vui lòng nhập serial.');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/warranties/lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: lookupType, query }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Không thể tra cứu bảo hành.');
      }
      if (!payload?.found) {
        setNotFound(true);
        return;
      }
      setResults(Array.isArray(payload.warranties) ? payload.warranties : [payload.warranty]);
    } catch (lookupError) {
      setError(lookupError instanceof Error ? lookupError.message : 'Không thể tra cứu bảo hành.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Header />
      <main className="flex-1 py-8">
        <div className="mx-auto w-full max-w-[980px] px-4">
          <nav className="mb-5 text-sm text-slate-500">
            <Link href="/" className="hover:text-primary">
              Trang chủ
            </Link>
            <span className="mx-2">/</span>
            <span className="font-medium text-slate-800">Tra cứu bảo hành</span>
          </nav>

          <section className="overflow-hidden rounded-2xl border border-emerald-200/70 bg-white shadow-[0_12px_34px_rgba(15,118,110,0.08)]">
            <div className="border-b border-emerald-100 bg-gradient-to-r from-emerald-50 to-white px-5 py-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-emerald-700">
                Hỗ trợ sau bán hàng
              </p>
              <h1 className="mt-1 text-2xl font-black text-slate-900">Tra cứu bảo hành</h1>
              <p className="mt-1 max-w-2xl text-sm text-slate-600">
                Chọn tra cứu theo số điện thoại khách hàng hoặc serial trên sản phẩm để kiểm tra
                thông tin bảo hành đã được DN TECH ghi nhận.
              </p>
            </div>

            <div className="grid gap-6 p-5 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-slate-100 p-1">
                  {[
                    ['phone', 'Số điện thoại'],
                    ['serial', 'Serial sản phẩm'],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        setLookupType(value as LookupType);
                        setQuery('');
                        setError('');
                        setNotFound(false);
                        setResults([]);
                      }}
                      className={`rounded-lg px-3 py-2 text-sm font-bold transition ${
                        lookupType === value
                          ? 'bg-white text-emerald-700 shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <label className="block space-y-2">
                  <span className="text-sm font-semibold text-slate-800">
                    {lookupType === 'phone' ? 'Số điện thoại' : 'Serial sản phẩm'}
                  </span>
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={
                      lookupType === 'phone' ? 'Ví dụ: 0901234567' : 'Ví dụ: DNTECH-ABC123'
                    }
                    className={`w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base font-semibold text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 ${
                      lookupType === 'serial' ? 'uppercase' : ''
                    }`}
                  />
                </label>

                {error ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                    {error}
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex w-full items-center justify-center rounded-xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-emerald-600/18 transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? 'Đang tra cứu...' : 'Tra cứu ngay'}
                </button>

                <p className="text-xs leading-5 text-slate-500">
                  Nếu thông tin chưa tìm thấy, vui lòng liên hệ DN TECH kèm số điện thoại mua hàng,
                  serial sản phẩm để được kiểm tra thủ công.
                </p>
              </form>

              <div className="min-h-[280px] rounded-2xl border border-slate-200 bg-slate-50 p-4">
                {!results.length && !notFound ? (
                  <div className="flex h-full min-h-[240px] items-center justify-center text-center text-sm text-slate-500">
                    Kết quả bảo hành sẽ hiển thị tại đây sau khi nhập thông tin tra cứu.
                  </div>
                ) : null}

                {notFound ? (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-800">
                    <h2 className="text-lg font-bold">Chưa tìm thấy thông tin bảo hành</h2>
                    <p className="mt-2 text-sm leading-6">
                      Thông tin này chưa có trong hệ thống hoặc đang được kiểm tra. Bạn có thể liên
                      hệ bộ phận hỗ trợ để DN TECH đối soát theo hóa đơn mua hàng.
                    </p>
                  </div>
                ) : null}

                {results.length ? (
                  <div className="space-y-4">
                    {results.map((result) => (
                      <div
                        key={result.imei_number || result.serial_number}
                        className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4"
                      >
                        <div
                          className={`rounded-xl border px-4 py-3 text-sm font-bold ${toneClass(result.warranty_state?.tone)}`}
                        >
                          {result.warranty_state?.label || 'Thông tin bảo hành'}
                        </div>

                        <div>
                          <h2 className="text-xl font-black text-slate-900">
                            {result.product_name}
                          </h2>
                          <p className="mt-1 text-sm text-slate-500">
                            Serial: {result.imei_number || result.serial_number}
                          </p>
                        </div>

                        <dl className="grid gap-3 sm:grid-cols-2">
                          {[
                            ['Khách hàng', result.customer_name || '-'],
                            ['Số điện thoại', result.customer_phone || '-'],
                            ['Địa chỉ', result.customer_address || '-'],
                            ['Bắt đầu bảo hành', formatDate(result.warranty_start)],
                            ['Hết hạn bảo hành', formatDate(result.warranty_end)],
                          ].map(([label, value]) => (
                            <div key={label} className="rounded-xl bg-slate-50 px-4 py-3">
                              <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                                {label}
                              </dt>
                              <dd className="mt-1 font-semibold text-slate-900">{value}</dd>
                            </div>
                          ))}
                        </dl>

                        {result.notes ? (
                          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-700">
                            {result.notes}
                          </div>
                        ) : null}

                        <button
                          type="button"
                          onClick={() => setContactOpen(true)}
                          className="inline-flex w-full items-center justify-center rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-emerald-700"
                        >
                          Liên hệ bảo hành
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />

      {contactOpen ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/45 px-4 py-6">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-black text-slate-900">Liên hệ bảo hành</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Chọn kênh hỗ trợ phù hợp để DN TECH tiếp nhận nhanh hơn.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setContactOpen(false)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              >
                Đóng
              </button>
            </div>

            <div className="mt-5 grid gap-3">
              <a
                href="tel:0337960997"
                className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 font-bold text-emerald-700 transition hover:bg-emerald-100"
              >
                Hotline: 0337960997
              </a>
              <a
                href="https://fanpage.dntechstore.com"
                target="_blank"
                rel="noreferrer"
                className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 font-bold text-slate-800 transition hover:bg-slate-100"
              >
                Fanpage: fanpage.dntechstore.com
              </a>
              <a
                href="https://zalo.me/0337960997"
                target="_blank"
                rel="noreferrer"
                className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 font-bold text-slate-800 transition hover:bg-slate-100"
              >
                Zalo: 0337960997
              </a>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
